import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useTrashedReferralPartners,
  useRestoreReferralPartners,
  usePermanentlyDeleteReferralPartners,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
  Loader2,
  AlertCircle,
  Search,
  X,
  RotateCcw,
  Trash2,
  ArrowLeft,
  Handshake,
} from 'lucide-react';
import type { ReferralPartner } from '../../services/httpServices/referralService';

const LIMIT = 20;

/** Human label for a partner's type. */
function typeLabel(partner: ReferralPartner): string {
  if (partner.type === 'DOCTOR') return 'Doctor';
  if (partner.type === 'EMPLOYEE') return 'Employee';
  return 'Other';
}

/**
 * Trash view for the referral-partner registry. Lists soft-deleted partners
 * with search + pagination and lets the user restore them or (when they have
 * no financial history) delete them permanently.
 */
export function ReferralPartnersTrash({ embedded }: { embedded?: boolean }) {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmTarget, setConfirmTarget] = useState<{ mode: 'single' | 'bulk'; partner?: ReferralPartner } | null>(
    null,
  );
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useTrashedReferralPartners(page, LIMIT, debouncedSearch.trim() || undefined);
  const restorePartners = useRestoreReferralPartners();
  const permanentDelete = usePermanentlyDeleteReferralPartners();

  const partners = data?.data || [];
  const meta = data?.meta;

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [debouncedSearch]);

  const allSelected = partners.length > 0 && partners.every((p) => selectedIds.includes(p.id));
  const someSelected = selectedIds.length > 0;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !partners.some((p) => p.id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...partners.map((p) => p.id)])));
    }
  };

  const toggleSelectOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleRestore = async (id: string) => {
    try {
      await restorePartners.mutateAsync([id]);
      setSelectedIds((prev) => prev.filter((x) => x !== id));
      toast.success('Referral partner restored');
    } catch {
      toast.error('Failed to restore referral partner');
    }
  };

  const handleBulkRestore = async () => {
    try {
      const res: any = await restorePartners.mutateAsync(selectedIds);
      const restored = res?.data?.restored?.length ?? selectedIds.length;
      toast.success(`Restored ${restored} referral partner${restored === 1 ? '' : 's'}`);
      setSelectedIds([]);
    } catch {
      toast.error('Failed to restore some referral partners');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    try {
      if (confirmTarget.mode === 'single') {
        await permanentDelete.mutateAsync([confirmTarget.partner!.id]);
        setSelectedIds((prev) => prev.filter((x) => x !== confirmTarget.partner!.id));
        toast.success('Referral partner permanently deleted');
      } else {
        const res: any = await permanentDelete.mutateAsync(selectedIds);
        const deleted = res?.data?.deleted?.length ?? selectedIds.length;
        const blocked = res?.data?.blocked?.length ?? 0;
        if (blocked > 0) {
          toast.warning(`Permanently deleted ${deleted} partner(s). ${blocked} had financial history.`);
        } else {
          toast.success(`Permanently deleted ${deleted} referral partner${deleted === 1 ? '' : 's'}`);
        }
        setSelectedIds([]);
      }
    } catch {
      if (confirmTarget.mode === 'bulk') toast.error('Failed to delete some referral partners');
      else toast.error('Failed to delete referral partner');
    } finally {
      setConfirmTarget(null);
    }
  };

  const busy = restorePartners.isPending || permanentDelete.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      {!embedded && (
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/referrals')}
            className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
            title="Back to Referrals"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-danger/10 rounded-lg">
              <Trash2 className="w-5 h-5 text-danger" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-text">Referral Partners Trash</h1>
              <p className="text-xs text-text-muted">Restore referral partners or delete them permanently</p>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search trashed partners by name, mobile or code..."
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {someSelected && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkRestore}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-success text-white text-sm font-medium hover:bg-success/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {restorePartners.isPending ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              Restore
            </button>
            <button
              onClick={() => setConfirmTarget({ mode: 'bulk' })}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-danger text-white text-sm font-medium hover:bg-danger/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {permanentDelete.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete permanently
            </button>
            <button
              onClick={() => setSelectedIds([])}
              disabled={busy}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load trashed referral partners</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={partners.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Mobile</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Default %</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Trashed</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {partners.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-16 text-center">
                      <Handshake className="w-10 h-10 mx-auto mb-3 text-text-muted/40" />
                      <p className="text-sm font-medium text-text-muted mb-1">
                        {search ? 'No trashed partners match your search' : 'Trash is empty'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  partners.map((partner) => (
                    <tr key={partner.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(partner.id)}
                          onChange={() => toggleSelectOne(partner.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-text">{partner.name}</td>
                      <td className="px-4 py-3 text-sm text-text-muted">{typeLabel(partner)}</td>
                      <td className="px-4 py-3 text-sm font-mono text-text-muted">{partner.code}</td>
                      <td className="px-4 py-3 text-sm text-text-muted">{partner.mobileNumber || '—'}</td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {Number(partner.defaultSharePercent) || 0}%
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {partner.deletedAt ? new Date(partner.deletedAt).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleRestore(partner.id)}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors disabled:opacity-50"
                            title="Restore"
                          >
                            <RotateCcw size={16} />
                          </button>
                          <button
                            onClick={() => setConfirmTarget({ mode: 'single', partner })}
                            disabled={busy}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                            title="Delete permanently"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {meta && (
            <Pagination
              page={meta.page}
              totalPages={meta.totalPages}
              total={meta.total}
              limit={meta.limit}
              onPageChange={setPage}
              itemName="trashed partners"
            />
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmTarget !== null}
        title={confirmTarget?.mode === 'bulk' ? 'Delete partners permanently' : 'Delete partner permanently'}
        description={
          confirmTarget?.mode === 'bulk' ? (
            `Permanently delete ${selectedIds.length} selected referral partner(s)? This cannot be undone.`
          ) : (
            <>
              Permanently delete <strong>{confirmTarget?.partner?.name}</strong>? This cannot be undone.
              Partners with revenue/payout history are protected and will be skipped.
            </>
          )
        }
        confirmLabel="Delete permanently"
        loading={busy}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}
